/**
 * AutoGenerateButton Component
 *
 * Button that triggers auto-generation of calendar resources from agent context.
 * Calls the auto-generate endpoint and passes extracted resources to the parent
 * via the onGenerated callback for preview before creation.
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4
 * - Requirement 1.1: Display Auto_Generate_Button labeled "Auto-generar desde contexto"
 * - Requirement 1.2: Disabled with tooltip when agent has no context
 * - Requirement 1.3: Initiates auto-generation and shows loading indicator on click
 * - Requirement 1.4: Disabled while generation is in progress
 */

import { useState } from 'react';
import { apiPost, ApiHttpError, TENANT_TOKEN_KEY } from '../../../api/client';
import { tokens } from '../../../design-system/tokens';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ExtractedResource {
  name: string;
  description: string | null;
  capacity: number;
  suggestedTypeName: string;
  customAttributes?: Record<string, string>;
}

export interface AutoGenerateButtonProps {
  agentId: string;
  hasContext: boolean;
  disabled?: boolean;
  onGenerated: (resources: ExtractedResource[]) => void;
  onError: (message: string) => void;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconSparkle() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSpinner() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      style={{ animation: 'spin 1s linear infinite' }}
    >
      <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AutoGenerateButton({
  agentId,
  hasContext,
  disabled = false,
  onGenerated,
  onError,
}: AutoGenerateButtonProps) {
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);

  const isDisabled = !hasContext || disabled || loading;

  const handleClick = async () => {
    if (isDisabled) return;

    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) return;

    setLoading(true);
    try {
      const data = await apiPost<{ resources: ExtractedResource[] }>(
        `/api/agents/${agentId}/calendar/resources/auto-generate`,
        {},
        token,
        { showErrorGrowl: false }
      );
      onGenerated(data.resources);
    } catch (err) {
      const message = getErrorMessage(err);
      onError(message);
    } finally {
      setLoading(false);
    }
  };

  const tooltipText = !hasContext
    ? 'Se requiere contexto del agente'
    : undefined;

  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
    border: `1px solid ${tokens.colors.primary[500]}`,
    borderRadius: tokens.borderRadius.md,
    backgroundColor: hovered && !isDisabled ? tokens.colors.primary[50] : 'transparent',
    color: isDisabled ? tokens.colors.neutral[400] : tokens.colors.primary[600],
    fontSize: tokens.typography.fontSize.sm,
    fontWeight: tokens.typography.fontWeight.medium,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: `background-color ${tokens.transitions.fast}, border-color ${tokens.transitions.fast}, color ${tokens.transitions.fast}`,
    opacity: isDisabled ? 0.6 : 1,
    borderColor: isDisabled ? tokens.colors.neutral[300] : tokens.colors.primary[500],
    whiteSpace: 'nowrap',
    position: 'relative',
    fontFamily: 'inherit',
    lineHeight: 1.5,
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        style={buttonStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label="Auto-generar desde contexto"
        title={tooltipText}
      >
        {loading ? <IconSpinner /> : <IconSparkle />}
        {loading ? 'Generando...' : 'Auto-generar desde contexto'}
      </button>

      {/* Inline keyframes for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getErrorMessage(err: unknown): string {
  if (err instanceof ApiHttpError) {
    switch (err.status) {
      case 400:
        return err.message || 'El agente no tiene documentos ni URLs con texto extraído.';
      case 504:
        return 'El modelo tardó demasiado en responder. Intenta nuevamente.';
      case 502:
        return 'No se pudo interpretar la respuesta del modelo. Intenta nuevamente.';
      default:
        return err.message || 'Ocurrió un error inesperado.';
    }
  }
  if (err instanceof Error && err.name === 'TimeoutError') {
    return 'La solicitud tardó demasiado. Verifica tu conexión e intenta nuevamente.';
  }
  return 'Error de conexión. Verifica tu red e intenta nuevamente.';
}

export default AutoGenerateButton;
